// 📄 pages/Home/HomeLanding.tsx
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, Cloud, Building2, BarChart3, GraduationCap, Plus, FolderKanban, LucideIcon, Code2, Bookmark, FolderPlus, ChevronRight } from 'lucide-react';
import { useAuth } from '../../context/AuthContext.tsx';
import OneHubLogo from '../../components/ui/OneHubLogo.tsx';
import OrgHomeWidget from './OrgHomeWidget.tsx';
import { TicketsPorAtenderTarjeta } from '../Soporte/TicketsPorAtender.tsx';
import HerramientasRail from './HerramientasRail.tsx';
import { useUserProjects } from '../../context/ProjectsContext.tsx';
import { MODULOS, ModuloId, modulosDe } from '../Proyectos/modulosConfig.ts';

interface HomeCard {
  id: string;
  title: string;
  description: string;
  icon: LucideIcon;
  featured?: boolean;
  requiresAuth: boolean;
  // Si está presente, la tarjeta navega a esta ruta (React Router) en vez
  // de usar el switch de páginas basado en estado (setPage) — para
  // módulos que viven fuera de OperativeApp, con URL propia.
  href?: string;
  // Módulos sin funcionalidad real todavía (2026-08-20, a pedido del
  // usuario) — visibles en QAS para seguir probando/construyéndolos,
  // ocultos en PRD para no confundir a usuarios reales con tarjetas que no
  // llevan a nada (ver REACT_APP_MOSTRAR_MODULOS_EN_DESARROLLO en
  // .env.development/.env.production).
  enDesarrollo?: boolean;
}

const MOSTRAR_MODULOS_EN_DESARROLLO = process.env.REACT_APP_MOSTRAR_MODULOS_EN_DESARROLLO === 'true';

// Marca aquí cuál tarjeta se destaca como "Más usado" — solo mueve el flag.
const HOME_CARDS: HomeCard[] = [
  {
    id: 'ariba',
    title: 'SAP Ariba',
    description: 'Integraciones, APIs y automatización de procesos Ariba.',
    icon: Package,
    requiresAuth: true
  },
  {
    id: 'btp',
    title: 'SAP BTP',
    description: 'Integration Suite, iFlows y desarrollo cloud.',
    icon: Cloud,
    // Visible también en PRD desde 2026-10-22: Integration Suite >
    // Repositorio de iFlows ya funciona. Adentro (BTPHome.tsx e
    // IntegrationSuiteHome.tsx) PRD solo muestra lo disponible.
    requiresAuth: true
  },
  {
    id: 'pruebas-apis',
    // "Mis APIs" (2026-08-20, a pedido del usuario) — a diferencia de
    // "Pruebas APIs" dentro de un proyecto (apis-test, ver menuConfig.tsx),
    // esta es la entrada SIN clienteId/projectId: lo que el usuario arma
    // acá se guarda a su propia cuenta (usuarios/{uid}/credenciales/apis,
    // ver obtenerMisApis/guardarMisApis en clienteConexionService.ts y el
    // branch `esPersonal = !clienteId` en AribaApiTester.tsx), no a un
    // proyecto — el nombre anterior no dejaba eso claro.
    title: 'Mis APIs',
    description: 'Tus credenciales y pruebas de API personales (Ariba, S/4HANA, Docusign y más).',
    icon: Code2,
    requiresAuth: true
  },
  {
    id: 's4',
    title: 'S/4HANA Cloud',
    description: 'APIs, OData y procesos empresariales.',
    icon: Building2,
    requiresAuth: true,
    enDesarrollo: true
  },
  {
    id: 'erp',
    title: 'ERP',
    description: 'Integraciones y flujos empresariales.',
    icon: BarChart3,
    requiresAuth: true,
    enDesarrollo: true
  },
  {
    id: 'study',
    title: 'Estudio',
    description: 'Aprende SAP, integraciones y buenas prácticas.',
    icon: GraduationCap,
    featured: true,
    requiresAuth: false,
    enDesarrollo: true
  },
];

interface HomeLandingProps {
  requireAuth: (callback: () => void) => void;
  setPage: (page: string) => void;
}

// Proyectos recientes en "Tu espacio de trabajo" (plan-proyectos-generales, G3).
const RECIENTES = 4;
const millis = (ts: any) => (ts?.toMillis ? ts.toMillis() : 0);

export default function HomeLanding({ requireAuth, setPage }: HomeLandingProps) {
  const { esClienteExterno } = useAuth();
  const navigate = useNavigate();
  const { proyectos } = useUserProjects();
  const recientes = proyectos
    .filter((p) => !p.archivado)
    .sort((a, b) => millis(b.fechaCreacion) - millis(a.fechaCreacion))
    .slice(0, RECIENTES);

  // Cuenta externa (rol "cliente" en al menos un proyecto): no le mostramos
  // la grilla de módulos internos (Ariba/BTP/S4/ERP/Estudio) — son
  // herramientas de consultoría, no algo a lo que un cliente deba acceder.
  // Solo le damos un acceso directo a Mis Proyectos, donde de todas formas
  // solo va a ver los proyectos donde es miembro (ya lo bloquean las reglas
  // de Firestore).
  if (esClienteExterno) {
    return (
      <main className="p-8 flex flex-col items-center">
        <OneHubLogo size={56} className="mb-4" />
        <h1 className="text-4xl font-bold mb-4 text-blue-400">
          Bienvenido a OneHub
        </h1>
        <p className="text-gray-300 mb-8 text-center max-w-xl">
          Desde acá podés ver el avance de tus proyectos, incidentes reportados y horas registradas por el equipo.
        </p>
        <div
          onClick={() => navigate('/proyectos')}
          className="w-full max-w-sm bg-gray-800 border border-blue-400/40 p-6 rounded-2xl cursor-pointer transition-all duration-150 hover:border-blue-400 hover:-translate-y-0.5 hover:shadow-lg text-center"
        >
          <div className="w-12 h-12 rounded-full flex items-center justify-center mb-4 mx-auto bg-[rgba(91,155,240,0.12)]">
            <FolderKanban className="w-6 h-6 text-blue-400" />
          </div>
          <h2 className="text-xl font-semibold mb-2">Mis Proyectos</h2>
          <p className="text-sm text-gray-400">Ver el estado de los proyectos donde participas.</p>
        </div>
        {/* Puede ser cliente en un proyecto y del equipo en otro. */}
        <TicketsPorAtenderTarjeta />
      </main>
    );
  }

  return (
    <div className="p-8">
      {/* Riel de Herramientas (2026-08-27) + contenido principal, como
          composición única centrada — ver HerramientasRail.tsx: en desktop
          es un riel vertical sticky que colapsa/expande, en mobile cae a
          una fila horizontal de chips arriba del contenido. */}
      <div className="flex flex-col md:flex-row items-stretch gap-6">
        <HerramientasRail requireAuth={requireAuth} setPage={setPage} />

        <main className="flex-1 min-w-0 flex flex-col items-center">
          <OneHubLogo size={56} className="mb-4" />
          <h1 className="text-4xl font-bold mb-4 text-blue-400">
            Bienvenido a OneHub
          </h1>

          <p className="text-gray-300 mb-8 text-center max-w-2xl">
            Gestiona tus proyectos, tu equipo y tus herramientas en un solo lugar.
          </p>

          <OrgHomeWidget />
          <TicketsPorAtenderTarjeta />

          {/* Tu espacio de trabajo (G3): lo que sirve para cualquier proyecto va primero. */}
          <section className="w-full max-w-6xl mt-8">
            <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Tu espacio de trabajo</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div
                onClick={() => requireAuth(() => navigate('/proyectos/nuevo'))}
                className="bg-blue-500/10 border-2 border-blue-400/60 p-6 rounded-2xl cursor-pointer transition-all duration-150 hover:border-blue-400 hover:-translate-y-0.5 hover:shadow-lg"
              >
                <div className="w-12 h-12 rounded-full flex items-center justify-center mb-4 bg-[rgba(91,155,240,0.15)]">
                  <FolderPlus className="w-6 h-6 text-blue-400" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Nuevo proyecto</h3>
                <p className="text-sm text-gray-400">Un proyecto general, de desarrollo de software o de SAP Ariba.</p>
              </div>

              <div className="bg-gray-800 border border-white/10 p-6 rounded-2xl flex flex-col">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center bg-[rgba(91,155,240,0.12)]">
                      <FolderKanban className="w-6 h-6 text-blue-400" />
                    </div>
                    <h3 className="text-xl font-semibold">Mis proyectos</h3>
                  </div>
                </div>
                {recientes.length === 0 ? (
                  <p className="text-sm text-gray-400 flex-1">Todavía no tienes proyectos. Crea el primero.</p>
                ) : (
                  <ul className="space-y-1 flex-1">
                    {recientes.map((p) => (
                      <li key={p.id}>
                        <button
                          type="button"
                          onClick={() => navigate(`/proyecto/${p.id}`)}
                          className="w-full flex items-center gap-2 text-left px-2 py-1.5 rounded-lg hover:bg-white/5"
                        >
                          <span className="text-sm leading-none">{MODULOS[(modulosDe(p).includes('sap-ariba') ? 'sap-ariba' : 'gestion') as ModuloId].icon}</span>
                          <span className="flex-1 min-w-0">
                            <span className="block text-sm text-gray-200 truncate">{p.nombre}</span>
                            <span className="block text-[11px] text-gray-500 truncate">{p.clienteId ? p.clienteNombre || 'Cliente' : 'Proyecto interno'}</span>
                          </span>
                          <ChevronRight className="w-4 h-4 text-gray-600" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                <button type="button" onClick={() => navigate('/proyectos')} className="mt-3 text-xs text-blue-400 hover:text-blue-300 text-left">
                  Ver todos los proyectos →
                </button>
              </div>

              <div
                onClick={() => requireAuth(() => navigate('/mi-espacio'))}
                className="bg-gray-800 border border-white/10 p-6 rounded-2xl cursor-pointer transition-all duration-150 hover:border-blue-400 hover:-translate-y-0.5 hover:shadow-lg"
              >
                <div className="w-12 h-12 rounded-full flex items-center justify-center mb-4 bg-[rgba(91,155,240,0.12)]">
                  <Bookmark className="w-6 h-6 text-blue-400" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Mi Espacio</h3>
                <p className="text-sm text-gray-400">Tus notas, código, contactos y archivos personales, con búsqueda inteligente.</p>
              </div>
            </div>
          </section>

          {/* Módulos especializados (G3): catálogo visible, no oculto. Se activan por proyecto. */}
          <section className="w-full max-w-6xl mt-10">
            <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Módulos especializados</h2>
            <p className="text-xs text-gray-500 mb-3">Herramientas para tecnologías específicas. En un proyecto, se activan desde Editar Proyecto.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 w-full">
            {HOME_CARDS.filter((card) => !card.enDesarrollo || MOSTRAR_MODULOS_EN_DESARROLLO).map((card) => {
              const Icon = card.icon;
              const handleClick = () => {
                const go = () => (card.href ? navigate(card.href) : setPage(card.id));
                card.requiresAuth ? requireAuth(go) : go();
              };

              return (
                <div
                  key={card.id}
                  onClick={handleClick}
                  className={`relative bg-gray-800 p-6 rounded-2xl cursor-pointer transition-all duration-150 hover:border-blue-400 hover:-translate-y-0.5 hover:shadow-lg ${
                    card.featured ? 'border-2 border-blue-400' : 'border border-white/10'
                  }`}
                >
                  {card.featured && (
                    <span className="absolute -top-3 left-4 bg-[rgba(91,155,240,0.15)] text-blue-400 text-[10px] font-semibold px-2.5 py-1 rounded-full border border-blue-400/30">
                      Más usado
                    </span>
                  )}

                  <div className="w-12 h-12 rounded-full flex items-center justify-center mb-4 bg-[rgba(91,155,240,0.12)]">
                    <Icon className="w-6 h-6 text-blue-400" />
                  </div>

                  <h3 className="text-xl font-semibold mb-2">{card.title}</h3>
                  <p className="text-sm text-gray-400">{card.description}</p>
                </div>
              );
            })}

            {/* Tarjeta placeholder para balancear el grid — no interactiva */}
            <div className="flex flex-col items-center justify-center text-center p-6 rounded-2xl border-2 border-dashed border-white/15 text-gray-500 min-h-[168px]">
              <Plus className="w-8 h-8 mb-2" />
              <p className="text-sm">Más módulos próximamente</p>
            </div>
          </div>
          </section>
        </main>
      </div>
    </div>
  );
}
